import React, { createContext, useReducer } from 'react';
import axios from '../../axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initialState = {
    companies:{
        data: []
    },
    registered: {
        data: [],
        total: 0
    },
    unregistered: {
        data: [],
        total: 0
    }
};

const reducer = (state, action) => {
    switch (action.type) {
        case 'GET_BIPO_COMPANIES':
            return {
                ...state,
                companies: action.payload
            };
        case 'GET_SYSTEM_MENUS':
            return {
                ...state,
                menus: action.payload
            };
        case 'GET_ACCESS_MENUS':
            return {
                ...state,
                access: action.payload
            };
        case 'GET_REGISTERED_ACCOUNTS':
            return {
                ...state,
                registered: action.payload
            };

        case 'GET_UNREGISTERED_ACCOUNTS':
            return {
                ...state,
                unregistered: action.payload
            };

        default:
            return state;
    }
};

const AccountContext = createContext({
    companies: { data: [] },
    registered: { data: [], total: 0 },
    unregistered: { data: [], total: 0 },
    account: {},
    menus: { data: [] },
    access: { data: [] },
    GetBipoCompanies: () => {},
    GetMappingCompanies: () => {},
    GetAccountInfo: () => {},
    GetRegisteredAccounts: () => {},
    GetUnregisteredAccounts: () => {},
    GetSystemMenus: () => {},
    GetAccountMenus: () => {},
    RegisterAccounts: () => {},
    ImportAccounts: () => {},
    PostActivating: () => {},
    UpdateAccount: () => {},
    UpdateSecret: () => {},
    GenerateSecret: () => {},
    DeleteAccount: () => {},
    RestoreAccount: () =>  {},
});

export const AccountProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const GetBipoCompanies = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_INFORMATIONS}bipo/companies`);
            if (res.data.success) {
                dispatch({
                    type: 'GET_BIPO_COMPANIES',
                    payload: {
                        data: res.data.data,
                    }
                });
                return res.data;
            }

            return { data: [] };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load company items !');
            return { data: [] };
        }
    };

    const GetMappingCompanies = async (payload) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_INFORMATIONS}/mapping/companies`);
            if (res.data.success) {
                return res.data.payload;
            }

            return { data: [] };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load list menus !');
            return { data: [] };
        }
    };

    const GetAccountInfo = async (payload) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_AUTH_API}/account/info/${payload}`);
            console.log("API RESPONSE:", res.data);
            if (res.data.success) {
                return res.data.payload;
            }

            return {}
        } catch (error) {
            console.error(error);
            toast.error('Failed to load account information !');
            return {}
        }
    };

    const GetRegisteredAccounts = async ({ page = 1, limit = 10, search = '', company = '', status = '' }) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_AUTH_API}employee/registered`,{ params: { page, limit, search, company, status } });
            if (res.data.success) {
                dispatch({
                    type: 'GET_REGISTERED_ACCOUNTS',
                    payload: {
                        data: res.data.data,
                        total: res.data.total
                    }
                });
                return res.data;
            }

            return { data: [], total: 0 };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load registered accounts');
            return { data: [], total: 0 };
        }
    };

    const GetUnregisteredAccounts = async ({ page = 1, limit = 10, search = '', company = '' }) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_AUTH_API}employee/un-registered`,{ params: { page, limit, search, company } });
            if (res.data.success) {
                dispatch({
                    type: 'GET_UNREGISTERED_ACCOUNTS',
                    payload: {
                        data: res.data.data,
                        total: res.data.total
                    }
                });
                return res.data;
            }

            return { data: [], total: 0 };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load unregistered accounts');
            return { data: [], total: 0 };
        }
    };

    const GetSystemMenus = async () => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_AUTH_API}account/menus`);
            if (res.data.success) {
                dispatch({
                    type: 'GET_SYSTEM_MENUS',
                    payload: {
                        data: res.data.data
                    }
                });
                return res.data.data;
            }

            return { data: [] };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load list menus !');
            return { data: [] };
        }
    };

    const GetAccountMenus = async (payload) => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_AUTH_API}account/access/menus/${payload}`);
            if (res.data.success) {
                dispatch({
                    type: 'GET_ACCESS_MENUS',
                    payload: {
                        data: res.data.data
                    }
                });
                return res.data.data;
            }

            return { data: [] };
        } catch (error) {
            console.error(error);
            toast.error('Failed to load list menus !');
            return { data: [] };
        }
    };

    const ImportAccounts = async (payload) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_AUTH_API}account/imports`,{
                account: payload
            });

            if (res.data.success) {
                return {
                    success: true,
                    data: res.data.data,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || "Failed to import accounts !"
            };
        } catch (error) {
            console.error(error);

            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, please try again later !"
            };
        }
    };

    const RegisterAccounts = async (payload) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_AUTH_API}account/registers`, payload);
            if (res.data.success) {
                return {
                    success: true,
                    message: res.data?.message
                };
            }else{
                return {
                    success: false,
                    message: res.data?.message || "Failed to registering accounts !"
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, please try again later !"
            };
        }
    };

    const PostActivating = async (payload) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_AUTH_API}account/activation`,payload);

            if (res.data.success) {
                return {
                    success: true,
                    data: res.data.data,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || `Failed to ${Boolean(payload?.status) ? 'deactivate' : 'activate'} this account !`
            };
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    const UpdateAccount = async (payload) => {
        try {
            const res = await axios.put(`${process.env.REACT_APP_AUTH_API}account/updated/${payload?.employee_code}`,payload);

            if (res.data.success) {
                return {
                    success: true,
                    data: res.data.data,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || `Failed to update this account !`
            };
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    const UpdateSecret = async (payload) => {
        try {
            const res = await axios.put(`${process.env.REACT_APP_AUTH_API}account/secret/updated/${payload?.employee_code}`,payload);
            return res.data;
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    const GenerateSecret = async (payload) => {
        try {
            const res = await axios.put(`${process.env.REACT_APP_AUTH_API}account/generate/renew/secret`,payload);

            if (res.data.success) {
                return {
                    success: true,
                    data: res.data.data,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || `Failed to update this account !`
            };
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    const DeleteAccount = async (payload) => {
        try {
            const res = await axios.delete(`${process.env.REACT_APP_AUTH_API}account/deletion/${payload}`);

            if (res.data.success) {
                return {
                    success: true,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || `Failed to ${Boolean(payload?.status) ? 'deactivate' : 'activate'} this account !`
            };
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    const RestoreAccount = async (payload) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_AUTH_API}account/restoration/${payload}`);

            if (res.data.success) {
                return {
                    success: true,
                    data: res.data.data,
                    message: res.data.message
                };
            }

            return {
                success: false,
                message: res.data.message || `Failed to ${Boolean(payload?.status) ? 'deactivate' : 'activate'} this account !`
            };
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || "Internal server error, pleae try again later !"
            };
        }
    };

    return (
        <AccountContext.Provider
            value={{
                menus: state.menus,
                companies: state.companies,
                registered: state.registered,
                unregistered: state.unregistered,
                GetBipoCompanies,
                GetMappingCompanies,
                GetAccountInfo,
                GetRegisteredAccounts,
                GetUnregisteredAccounts,
                GetSystemMenus,
                GetAccountMenus,
                RegisterAccounts,
                ImportAccounts,
                PostActivating,
                UpdateAccount,
                UpdateSecret,
                GenerateSecret,
                DeleteAccount,
                RestoreAccount
            }}
        >
            {children}
            <ToastContainer position="top-right" />
        </AccountContext.Provider>
    );
};

export default AccountContext;
